import { ApolloError } from "@apollo/client"
import { ActivePageType, SendResetPasswordFieldsProps } from "./useResetPassword"
import { Form, SubmitButton, TextField } from "rentpost/components/form"
import * as yup from "yup"
import { AnimateWithVariants } from "rentpost/components/functional"

interface Props {
   onSubmit: (input: SendResetPasswordFieldsProps) => void
   loading: boolean
   error: ApolloError | undefined
   activePage: ActivePageType
}

// ===============
// Form Properties
// ===============
const initialValues: SendResetPasswordFieldsProps = {
   email: "",
}

const validationSchema = yup.object().shape({
   email: yup.string().email().required(),
})

const EmailForm: React.FC<Props> = (props) => {
   const { onSubmit, loading, error, activePage } = props

   const formProps = {
      initialValues,
      validationSchema,
      onSubmit,
      error,
      loading,
      showErrorHint: true,
   }

   return (
      <>
         {activePage === "email-form" && (
            <AnimateWithVariants animate="pageAnimate" initial="pageInitial">
               <h1>Reset Password</h1>
               <p className="description">
                  Please enter your email address and we will send you a link to reset your
                  password.
               </p>
               <Form {...formProps}>
                  <TextField name="email" label="Email" placeholder="email@company.com" />
                  <SubmitButton>Reset Password</SubmitButton>
               </Form>
            </AnimateWithVariants>
         )}
         {activePage === "email-sent-successfully" && (
            <AnimateWithVariants animate="pageAnimate" initial="pageInitial">
               <EmailSentSuccessfully />
            </AnimateWithVariants>
         )}
      </>
   )
}

const EmailSentSuccessfully = () => {
   return (
      <AnimateWithVariants animate="pageAnimate" initial="pageInitial" className="success-holder">
         <svg viewBox="0 0 177 177" fill="none" xmlns="http://www.w3.org/2000/svg" className="icon">
            <path
               d="M87.2304 120.771L4.58643 69.1461L90.3322 4.44897L173.198 69.1461L87.2304 120.771Z"
               fill="#EA7A15"
            />
            <rect x="22.0908" y="21.2129" width="132.585" height="96.2516" fill="#FFECC7" />
            <path d="M73.5692 113.64L4.72705 172.283V69.3387L73.5692 113.64Z" fill="#F4AE00" />
            <path d="M103.363 113.64L172.205 172.283V69.3387L103.363 113.64Z" fill="#F4AE00" />
            <path
               d="M171.955 172.834H5.29492L74.8311 114.106L88.6357 122.03L103.118 114.106L171.955 172.834Z"
               fill="#FFC612"
            />
            <path
               fillRule="evenodd"
               clipRule="evenodd"
               d="M75.4767 9.30358L63.4749 18.3718L41.0705 18.5805L18.6661 18.79L18.4455 35.7201L18.224 52.6502L9.18427 59.3435L0.144531 66.0368L0.309839 121.158L0.474356 176.279H88.2695H176.065L176.229 121.158L176.394 66.0368L167.355 59.3435L158.315 52.6502L158.094 35.7201L157.873 18.79L135.464 18.5805L113.055 18.3718L101.453 9.60439C95.0725 4.78285 89.3175 0.702294 88.665 0.536143C88.0125 0.370779 82.078 4.31589 75.4767 9.30358ZM95.6933 12.499L102.325 17.6088H88.2735H74.2215L78.0813 14.6102C86.0889 8.38936 87.4999 7.3893 88.2695 7.39009C88.7045 7.39009 92.0447 9.68943 95.6933 12.499ZM152.732 50.906V77.9044L120.955 97.5623C103.479 108.375 88.9497 117.318 88.6697 117.436C88.3905 117.554 73.7707 108.775 56.1816 97.9269L24.2028 78.2029L23.7084 64.2454C23.0053 44.4104 22.9254 25.0486 23.5439 24.4336C23.8334 24.1446 53.0194 23.9084 88.4016 23.9084H152.732V50.906ZM17.4797 67.2644C17.4797 72.134 17.2496 73.4719 16.491 73.0152C11.709 70.1347 9.05455 68.2873 8.84891 67.6952C8.60925 67.0038 15.7262 61.0397 16.8865 60.9594C17.2132 60.9365 17.4797 63.7745 17.4797 67.2644ZM164.234 64.1627C166.608 65.9471 168.551 67.507 168.551 67.6283C168.551 67.7503 167.363 68.5472 165.912 69.4C164.461 70.2528 162.325 71.5686 161.166 72.3246L159.059 73.6987V67.3085C159.059 63.7942 159.252 60.9184 159.489 60.9184C159.725 60.9184 161.861 62.3783 164.234 64.1627ZM37.5571 93.0526C54.9074 103.776 69.1404 112.892 69.1855 113.31C69.2717 114.101 6.46341 167.191 5.41778 167.212C5.09191 167.218 4.82457 146.14 4.82457 120.371C4.82457 94.6015 5.09191 73.5262 5.41778 73.5365C5.74365 73.5467 20.2069 82.3291 37.5571 93.0526ZM170.924 120.394V167.271L162.421 160.243C134.954 137.54 107.263 113.931 107.335 113.276C107.38 112.858 111.117 110.236 115.639 107.448C120.162 104.66 134.362 95.9031 147.195 87.9885C160.028 80.0731 170.617 73.5798 170.726 73.5577C170.834 73.5357 170.924 94.6117 170.924 120.394ZM81.9008 120.371C85.0417 122.32 87.9073 123.914 88.2695 123.914C88.6318 123.914 91.5867 122.265 94.836 120.248C99.8616 117.13 100.969 116.722 102.249 117.518C103.414 118.242 162.409 167.451 165.743 170.478C166.168 170.864 132.459 171.161 88.2853 171.161H10.0765L13.23 168.474C33.8943 150.865 74.912 116.827 75.4688 116.827C75.8659 116.827 78.7599 118.422 81.9008 120.371Z"
               fill="black"
            />
            <circle cx="149.793" cy="27.1327" r="26.6016" fill="#F53B3C" />
            <path
               d="M152.579 38.9608H148.187V25.0473H143.186V22.0659C144.682 22.0445 145.932 21.6705 146.936 20.9438C147.941 20.1958 148.55 19.2661 148.764 18.1547H152.579V38.9608Z"
               fill="white"
            />
         </svg>

         <h1>Check Your Email</h1>
         <p className="description" style={{ textAlign: "center" }}>
            We have sent you an email with a link to reset your password. Please check your inbox
            and follow the instructions in the email.
         </p>
      </AnimateWithVariants>
   )
}

export default EmailForm
